/**
 * Input — generic text/email/url/tel atom (ADR-0007).
 *
 * 7 visual states:
 *   1. default — нейтральная рамка
 *   2. hover — рамка `border-strong`
 *   3. focus — кольцо `border-focus/30`
 *   4. error — красная рамка + бледно-красный bg
 *   5. focus + error — толще красное кольцо (3px ring, danger-fg)
 *   6. disabled — приглушённый bg, текст `text-disabled`
 *   7. success — зелёная рамка + лёгкое кольцо
 *
 * Slots: leftSlot/rightSlot (для иконок). Совместно с `<Tooltip>` дают «i»-подсказки.
 */
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react'

const input = cva(
  [
    'block w-full rounded-lg border bg-input-bg-default text-input-text-default',
    'placeholder:text-text-muted',
    'transition-[border-color,box-shadow,background-color] duration-150',
    'focus:outline-none',
    'disabled:bg-surface-subtle disabled:text-text-disabled disabled:cursor-not-allowed disabled:opacity-60',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-3.5 text-sm',
        lg: 'h-12 px-4 text-base',
      },
      state: {
        // 1 + 2 + 3 — ring использует Tailwind alpha modifier на RGB-triple токенах,
        // theme-aware (см. tokens.css `--*-rgb`).
        default: [
          'border-input-border-default',
          'hover:border-border-strong',
          'focus:border-border-focus focus:ring-2 focus:ring-border-focus/35',
        ],
        // 4 + 5 (focus+error → толще ring 3px, более яркий красный)
        error: [
          'border-status-danger-border bg-status-danger-bg',
          'hover:border-status-danger-text',
          'focus:border-status-danger-text focus:ring-[3px] focus:ring-status-danger-text/45',
        ],
        // 7
        success: [
          'border-status-success-border',
          'hover:border-status-success-text',
          'focus:border-status-success-text focus:ring-2 focus:ring-status-success-text/35',
        ],
      },
    },
    defaultVariants: { size: 'md', state: 'default' },
  }
)

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof input> {
  /** @deprecated use `state="error"` instead. Kept for back-compat with FormField. */
  hasError?: boolean
  leftSlot?: ReactNode
  rightSlot?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size, state, hasError, leftSlot, rightSlot, ...rest },
  ref
) {
  // Back-compat: hasError trumps state.
  const resolvedState = hasError ? 'error' : state ?? 'default'
  const cls = input({ size, state: resolvedState, className: `${className ?? ''} ${leftSlot ? 'pl-10' : ''} ${rightSlot ? 'pr-10' : ''}` })
  if (leftSlot || rightSlot) {
    return (
      <div className="relative flex items-center">
        {leftSlot && <span className="absolute left-3 inline-flex pointer-events-auto">{leftSlot}</span>}
        <input
          ref={ref}
          aria-invalid={resolvedState === 'error' || undefined}
          className={cls}
          {...rest}
        />
        {rightSlot && <span className="absolute right-3 inline-flex pointer-events-auto">{rightSlot}</span>}
      </div>
    )
  }
  return <input ref={ref} aria-invalid={resolvedState === 'error' || undefined} className={cls} {...rest} />
})
